// src/components/ProjectManagement.jsx - Mit Network Manager Integration
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ProjectOverviewTable from "./ProjectOverviewTable";
import ProjectForm from "./ProjectForm";
import ProjectDetails from "./ProjectDetails";

import * as ProjectsService from "../services/azureProjectsService";
import ProjectPage from "./ProjectPage";
import Controlling from "./Controlling";
import StundenAuszug from "./StundenAuszug";
import { useFreelancerProjects } from "../hooks/useFreelancerProjects";
import { isFreelancer as checkIsFreelancer, canEdit } from "../utils/permissions";
import ProjektstandModal from "./ProjektstandModal";

const ProjectManagement = ({ selectedEmployee, employees = [], user, readOnlyControlling = false, initialSubTab = "overview" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectCode: urlProjectCode } = useParams();
  const [currentView, setCurrentView] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState("projects");
  const [offerten, setOfferten] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typeFilter, setTypeFilter] = useState("project");
  const [showProjectPage, setShowProjectPage] = useState(false);
  const [controllingProject, setControllingProject] = useState(null);
  const [stundenAuszugProject, setStundenAuszugProject] = useState(null);

  const [showCompleted, setShowCompleted] = useState(false);
  const [projektstandProject, setProjektstandProject] = useState(null);

  // Freelancer project filtering
  const { filterProjects, isFreelancerUser } = useFreelancerProjects(user);

  // Filter projects for freelancers
  const filteredProjects = useMemo(() => {
    return filterProjects(projects);
  }, [projects, filterProjects]);

  // Check if user can edit projects (freelancers cannot)
  const canEditProjects = user ? canEdit(user, 'projekte') : false;

  // Projekte beim ersten Laden
  useEffect(() => {
    loadProjects();
  }, [typeFilter]);

  // Handle navigation from finance dashboard to Stundenauszug
  useEffect(() => {
    if (location.state?.openStundenAuszug && projects.length > 0) {
      const projectCode = location.state.openStundenAuszug;
      const project = projects.find((p) => p.code === projectCode);

      if (project) {
        setStundenAuszugProject(project);
        // Clear the state to prevent reopening on subsequent renders
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, projects]);

  // Auto-load project based on URL param or localStorage
  useEffect(() => {
    if (projects.length === 0) return;

    const code = urlProjectCode || localStorage.getItem('lastProjectCode');

    if (initialSubTab === "controlling" && !controllingProject) {
      if (code) {
        const project = projects.find((p) => p.code === code);
        if (project) {
          localStorage.setItem('lastProjectCode', code);
          setControllingProject(project);
          // Ensure URL has the project code
          if (!urlProjectCode) {
            navigate(`/main-projects/controlling/${code}`, { replace: true });
          }
        }
      }
    } else if (initialSubTab === "stundenauszug" && !stundenAuszugProject) {
      if (code) {
        const project = projects.find((p) => p.code === code);
        if (project) {
          localStorage.setItem('lastProjectCode', code);
          setStundenAuszugProject(project);
          if (!urlProjectCode) {
            navigate(`/main-projects/stundenauszug/${code}`, { replace: true });
          }
        }
      }
    } else if (initialSubTab === "overview") {
      setControllingProject(null);
      setStundenAuszugProject(null);
    }
  }, [initialSubTab, projects, urlProjectCode]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const projectsData = await ProjectsService.getAllProjectsWithTags(
        typeFilter
      );
      setProjects(projectsData);
    } catch (err) {
      setError("Fehler beim Laden der Projekte: " + err.message);
      console.error("Load Projects Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    setSelectedProject(null);
    setCurrentView("create");
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setCurrentView("edit");
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setCurrentView("details");
  };

  const handleViewProjectPage = (project) => {
    // Open project page in new tab with URL: /projects/{code}-{title}/overview
    const slug = `${project.code}-${project.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
    window.open(`/projects/${slug}/overview`, '_blank');
  };

  const handleShowControlling = (project) => {
    localStorage.setItem('lastProjectCode', project.code);
    setControllingProject(project);
    navigate(`/main-projects/controlling/${project.code}`);
  };

  const handleShowStundenAuszug = (project) => {
    localStorage.setItem('lastProjectCode', project.code);
    setStundenAuszugProject(project);
    navigate(`/main-projects/stundenauszug/${project.code}`);
  };

  const handleSaveProject = async (projectData) => {
    try {
      setLoading(true);
      setError(null);

      const savedProject = await ProjectsService.saveProjectWithTags(projectData);
      await loadProjects();

      setSuccess("Projekt erfolgreich gespeichert!");
      setCurrentView("overview");
      setSelectedProject(null);

      return savedProject;
    } catch (err) {
      setError("Fehler beim Speichern: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (
      !window.confirm(
        "Sind Sie sicher, dass Sie dieses Projekt löschen möchten?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await ProjectsService.deleteProject(projectId);

      // Remove from local state
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setSuccess("Projekt erfolgreich gelöscht!");
      setCurrentView("overview");
      setSelectedProject(null);
    } catch (err) {
      setError("Fehler beim Löschen des Projekts: " + err.message);
      console.error("Delete Project Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentView("overview");
    setSelectedProject(null);
  };



  // Success/Error Messages ausblenden
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  return (
    <div className="project-management">
      {currentView === "overview" && !controllingProject && !stundenAuszugProject && (
        <div className="overview-container">
          <div className="page-header">
            <h1>Projekte</h1>
          </div>

          {error && <div className="error-message" onClick={() => setError(null)}>{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <ProjectOverviewTable
            projects={filteredProjects}
            selectedEmployee={selectedEmployee}
            employees={employees}
            user={user}
            onEdit={isFreelancerUser ? null : handleEditProject}
            onView={handleViewProject}
            onControlling={isFreelancerUser ? null : handleShowControlling}
            onStundenAuszug={isFreelancerUser ? null : handleShowStundenAuszug}
            onProjektstand={isFreelancerUser ? null : (project) => setProjektstandProject(project)}
            onProjectPage={handleViewProjectPage}
            loading={loading}
            readOnly={readOnlyControlling || isFreelancerUser}

            typeFilter={typeFilter}
            setTypeFilter={isFreelancerUser ? () => {} : setTypeFilter}
            showCompleted={showCompleted}
            setShowCompleted={setShowCompleted}
            isFreelancer={isFreelancerUser}
          />

          {/* FAB Add Button - Hidden for freelancers */}
          {!isFreelancerUser && (
            <div className="team-fab">
              <button
                className="fab-add-button"
                onClick={handleCreateProject}
                title="Neues Projekt erfassen"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {currentView === "overview" && controllingProject && (
        <div className="controlling-inline-container">
          <div className="controlling-inline-header">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setControllingProject(null);
                navigate('/main-projects');
              }}
            >
              ← Zurück zur Übersicht
            </button>
          </div>
          <Controlling
            project={controllingProject}
            projectCode={controllingProject.code}
            employees={employees}
            isInline={true}
            allProjects={filteredProjects}
            onProjectChange={(newProject) => {
              localStorage.setItem('lastProjectCode', newProject.code);
              setControllingProject(newProject);
              navigate(`/main-projects/controlling/${newProject.code}`, { replace: true });
            }}
            readOnly={readOnlyControlling}
          />
        </div>
      )}

      {currentView === "overview" && !controllingProject && initialSubTab === "controlling" && (
        <div className="no-project-hint">
          <p>Projekt auswählen:</p>
          <select
            className="project-selector"
            value=""
            onChange={(e) => {
              const project = filteredProjects.find((p) => p.code === e.target.value);
              if (project) handleShowControlling(project);
            }}
          >
            <option value="" disabled>— Projekt wählen —</option>
            {filteredProjects
              .filter((p) => p.type === "project" || !p.type)
              .sort((a, b) => b.code.localeCompare(a.code))
              .map((p) => (
                <option key={p.code} value={p.code}>{p.code} - {p.title}</option>
              ))}
          </select>
        </div>
      )}

      {currentView === "overview" && stundenAuszugProject && (
        <div className="controlling-inline-container">
          <div className="controlling-inline-header">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setStundenAuszugProject(null);
                navigate('/main-projects');
              }}
            >
              ← Zurück zur Übersicht
            </button>
          </div>
          <StundenAuszug
            project={stundenAuszugProject}
            projectCode={stundenAuszugProject.code}
            employees={employees}
            readOnly={readOnlyControlling}
            allProjects={filteredProjects}
            onProjectChange={(newProject) => {
              localStorage.setItem('lastProjectCode', newProject.code);
              setStundenAuszugProject(newProject);
              navigate(`/main-projects/stundenauszug/${newProject.code}`, { replace: true });
            }}
          />
        </div>
      )}

      {currentView === "overview" && !stundenAuszugProject && initialSubTab === "stundenauszug" && (
        <div className="no-project-hint">
          <p>Projekt auswählen:</p>
          <select
            className="project-selector"
            value=""
            onChange={(e) => {
              const project = filteredProjects.find((p) => p.code === e.target.value);
              if (project) handleShowStundenAuszug(project);
            }}
          >
            <option value="" disabled>— Projekt wählen —</option>
            {filteredProjects
              .filter((p) => p.type === "project" || !p.type)
              .sort((a, b) => b.code.localeCompare(a.code))
              .map((p) => (
                <option key={p.code} value={p.code}>{p.code} - {p.title}</option>
              ))}
          </select>
        </div>
      )}

      {(currentView === "create" || currentView === "edit") && (
        <ProjectForm
          project={currentView === "edit" ? selectedProject : null}
          employees={employees}
          onSave={handleSaveProject}
          onCancel={() => {
            setCurrentView("overview");
            setSelectedProject(null);
          }}
          loading={loading}
          isEditing={currentView === "edit"}
        />
      )}

      {currentView === "details" && selectedProject && (
        <ProjectDetails
          project={selectedProject}
          employees={employees}
          user={user}
          onBack={() => {
            setCurrentView("overview");
            setSelectedProject(null);
          }}
          onEdit={() => handleEditProject(selectedProject)}
          onSave={loadProjects}
          onProjectPage={handleViewProjectPage}
        />
      )}

      {/* Projektstand Modal */}
      {projektstandProject && (
        <ProjektstandModal
          projects={filteredProjects}
          initialProject={projektstandProject}
          user={user}
          onClose={() => setProjektstandProject(null)}
        />
      )}

      {showProjectPage && selectedProject && (
        <ProjectPage
          project={selectedProject}
          employees={employees}
          onBack={() => {
            setShowProjectPage(false);
            setSelectedProject(null);
            setCurrentView("overview");
          }}
          onEdit={() => {
            setShowProjectPage(false);
            handleEditProject(selectedProject);
          }}
          onDelete={async () => {
            await handleDeleteProject(selectedProject.id);
            setShowProjectPage(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
};

export default ProjectManagement;
